import { State, process } from '@progress/kendo-data-query';
import { formatNumber } from '@progress/kendo-intl';
import {
  GridDataStateChangeEvent,
  GridDetailRowProps,
  GridPagerSettings,
} from '@progress/kendo-react-grid';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import { TabStrip, TabStripSelectEventArguments, TabStripTab } from '@progress/kendo-react-layout';
import { TFunction } from 'i18next';
import { isEmpty, isNull, orderBy, sumBy, uniqBy } from 'lodash';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  useHoldAmountColumns,
  useHoldAmountDetailColumns,
} from 'src/assets/static/view_checkbook/holdAmountColumns';
import GridWrapper from 'src/components/GridWrapper';
import useViewCheckbook from 'src/hooks/useViewCheckbook';
import { GLCodePeriodBudgetResponse } from 'src/services/useGlCodePeriodBudgetApi';
import { GLCodesBudgetResponseObject } from 'src/services/useGlCodesBudgetApi';
import useHoldAmountApi, { IHoldAmountTransaction } from 'src/services/useHoldAmountApi';
import { round } from 'src/utils';
import exportToExcel from 'src/utils/exportToExcel';
import { GLPeriodDropdownChildren } from 'src/utils/transformation';
import { fixDateSort } from './CheckbookActualizedAmountTable';
import CheckbookBalanceToolBar from './CheckbookBalanceToolBar';

const DATA_ITEM_KEY: string = 'rowId';
const initialDataState: State = {
  skip: 0,
  take: 20,
  sort: [
    {
      field: 'date',
      dir: 'desc',
    },
  ],
};
const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 20, 50, 100],
};
const selectable: GridSelectableSettings = {
  enabled: true,
  drag: false,
  cell: false,
  mode: 'single',
};
const defaultProps = {
  tableTextId: 'Transactions',
  DataItemKey: DATA_ITEM_KEY,
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  selectable: selectable,
  pageable: gridPagerSettings,
} as const;

interface Props {
  glBudget: GLCodesBudgetResponseObject;
}
interface HoldAmountExcelData extends Omit<IHoldAmountTransaction, 'total' | 'items'> {
  glCode: string;
  fiscalYear: string | undefined;
  period: string | undefined;
  total: string;
  lineItem?: string;
  itemNum?: string;
  description?: string;
  amount?: string;
  companyName?: string;
}

export const checkbookBalanceTitle = (
  data: GLPeriodDropdownChildren,
  t: TFunction<'translation', undefined>,
  selectedGLBudget?: GLCodePeriodBudgetResponse,
  type?: string
) => (
  <div>
    <p className="font-bold mb-1">
      {t(
        `Checkbook Transactions - ${
          type === 'holdAmount'
            ? 'Planned'
            : type === 'committedAmount'
            ? 'Committed'
            : type === 'actualizedAmount'
            ? 'Actualized'
            : 'All'
        } Transactions ${
          type === 'allLog' ? ' - All GL Codes' : ` - ${selectedGLBudget?.description || 'GL Code'}`
        }`
      )}
    </p>
    <p className="text-xs font-medium">
      <span>
        {t('GL Code')}: <span className="font-bold">{selectedGLBudget?.code || 'ALL'}</span>
      </span>
      {type === 'logAmount' && (
        <>
          <span className="text-[#AAAAAA]"> | </span>
          <span>
            {t('Forecast Amount')}:{' '}
            <span className="font-bold">
              {formatNumber(selectedGLBudget?.budgetAmount || 0, 'c2')}
            </span>
          </span>
        </>
      )}
      <span className="text-[#AAAAAA]"> | </span>
      <span>
        {t('Fiscal year')}: <span className="font-bold">{data.fiscalYearDescription}</span>
      </span>
      <span className="text-[#AAAAAA]"> | </span>
      <span>
        {t('Accounting period')}: <span className="font-bold">{data.content}</span>
      </span>
    </p>
  </div>
);

const CheckbookHoldAmountTable: FC<Props> = ({ glBudget }) => {
  const { dropDownPeriodValue, holdAmountData, setHoldAmountData, t, selectedCompany } =
    useViewCheckbook();
  const holdAmountColumns = useHoldAmountColumns(true);
  const holdAmountDetailColumns = useHoldAmountDetailColumns();
  const { getHoldAmountTransaction, isHoldLoading } = useHoldAmountApi();
  const [dataState, setDataState] = useState(initialDataState);
  const [exportLoading, setExportLoading] = useState(false);

  const [selectedView, setSelectedView] = useState(0);
  const handleSelect = useCallback(
    (e: TabStripSelectEventArguments) => {
      const { selected } = e;
      setSelectedView(selected);
      setDataState(initialDataState);
    },
    [setSelectedView]
  );

  const { data, total } = useMemo(
    () => process(holdAmountData || [], fixDateSort(dataState)),
    [dataState, holdAmountData]
  );
  const { companyName } = selectedCompany;
  const onDataStateChange = useCallback((e: GridDataStateChangeEvent) => {
    setDataState(e.dataState);
  }, []);

  const handleClearFilter = useCallback(() => {
    setDataState((prev) => ({
      ...initialDataState,
      skip: prev.skip,
      take: prev.take,
    }));
  }, []);

  useEffect(() => {
    if (glBudget?.id && dropDownPeriodValue?.glPeriodId) {
      getHoldAmountTransaction({
        glCodeId: glBudget?.id,
        glPeriodId: dropDownPeriodValue?.glPeriodId,
      }).then((res) => {
        const data = uniqBy(
          orderBy(
            res.filter(
              (item) =>
                item.action.includes('Submitted') &&
                round(
                  sumBy(
                    res.filter((i) => i.poId === item.poId),
                    'total'
                  ),
                  2
                ) !== 0
            ),
            'actualDate',
            'desc'
          ),
          'poId'
        );
        setHoldAmountData(selectedView === 0 ? data : res);
      });
    }
  }, [
    glBudget?.id,
    dropDownPeriodValue?.glPeriodId,
    getHoldAmountTransaction,
    setHoldAmountData,
    selectedView,
  ]);

  const calculateExcelData = useCallback(
    (page: number) => {
      let excelData: HoldAmountExcelData[] = [];
      const rows = page === 1 ? (data as IHoldAmountTransaction[]) : holdAmountData;

      rows.forEach((obj) => {
        if (isNull(obj.items) || isEmpty(obj.items)) {
          const newData = {
            ...obj,
            glCode: glBudget?.code,
            fiscalYear: dropDownPeriodValue?.fiscalYearDescription,
            period: dropDownPeriodValue?.content,
            total: formatNumber(obj.total, 'c2'),
            companyName,
          };
          excelData.push(newData);
        } else {
          obj.items.forEach((item) => {
            const newData = {
              ...item,
              ...obj,
              glCode: glBudget?.code,
              fiscalYear: dropDownPeriodValue?.fiscalYearDescription,
              period: dropDownPeriodValue?.content,
              total: formatNumber(obj.total, 'c2'),
              amount: formatNumber(item.amount, 'c2'),
              companyName,
            };
            excelData.push(newData);
          });
        }
      });

      return excelData;
    },
    [
      companyName,
      data,
      dropDownPeriodValue?.content,
      dropDownPeriodValue?.fiscalYearDescription,
      glBudget?.code,
      holdAmountData,
    ]
  );

  const excelExport = useCallback(
    (page: number) => {
      const rows = calculateExcelData(page);
      const columns: string[] = [];
      const columnMapping: Record<string, string> = {};
      const excelColumns = [
        { field: 'companyName', title: t('Company Name') },
        { field: 'glCode', title: t('GL Code') },
        { field: 'fiscalYear', title: t('Fiscal Year #') },
        { field: 'period', title: t('Accounting Period #') },
        ...holdAmountColumns.filter((item) => item?.field !== 'expanded'),
        ...holdAmountDetailColumns,
      ];
      excelColumns.forEach((item) => {
        if (item.field === 'date') {
          columns.push('transactionDate');
          Object.assign(columnMapping, { transactionDate: item.title });
        } else {
          columns.push(item.field as string);
          Object.assign(columnMapping, { [item.field as string]: item.title });
        }
      });
      exportToExcel({
        columns,
        rows,
        fileName: t('Planned Amount Transactions'),
        columnMapping,
        sheetName: t('Planned Amount Line Items'),
      });
    },
    [calculateExcelData, holdAmountColumns, holdAmountDetailColumns, t]
  );

  const gridToolbarChildren = useMemo(
    () => (
      <CheckbookBalanceToolBar
        setExportLoading={setExportLoading}
        clearFilter={handleClearFilter}
        excelExport={excelExport}
        dataState={dataState}
      />
    ),
    [dataState, excelExport, handleClearFilter]
  );

  const DetailComponent = useCallback(
    (props: GridDetailRowProps) => (
      <GridWrapper
        dataSource={props.dataItem.items}
        columns={holdAmountDetailColumns}
        DataItemKey="field"
        dataState={dataState}
        className="no_Veritcal_ScrollBar"
      />
    ),
    [dataState, holdAmountDetailColumns]
  );

  return (
    <>
      <div className="pb-2">
        <TabStrip selected={selectedView} onSelect={handleSelect}>
          <TabStripTab contentClassName="!hidden" title={t('Show Current')} />
          <TabStripTab contentClassName="!hidden" title={t('Show All')} />
        </TabStrip>
      </div>

      <GridWrapper
        id="holdAmountTransactionGrid"
        columns={holdAmountColumns}
        dataSource={data}
        dataState={dataState}
        height={'60vh'}
        total={total}
        onDataStateChange={onDataStateChange}
        gridToolbarChildren={gridToolbarChildren}
        detail={DetailComponent}
        loading={isHoldLoading || exportLoading}
        isCustomExpandableRow
        {...defaultProps}
      />
    </>
  );
};

export default CheckbookHoldAmountTable;
